import { QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LOCAL_STORAGE_WORKSPACE_ID } from 'config/constants';
import { EVENT_RECORDS, SUMMARY_RECORDS } from 'config/endpoints/records';
import { get, post } from 'utils/axios';
import { GenericQueryOptions } from '../types';
import { Message, Records, Summary } from './contract';
import { CommentProps, OptimisticRecordsCommentProps, RecordCategory, RecordsProps } from './types';

const createNewComment = ({
  translate = false,
  text = '',
  user_email = '',
  date = '',
  category = RecordCategory.COMMENT,
}: Partial<Message>): Message => ({
  id: '',
  category,
  text,
  user_email,
  date,
  translate,
});

export const useInvalidateRecordsQueries = (eventId?: string) => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries(['SUMMARY', 'RECORDS', eventId]);

    setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ['RECORDS', eventId],
      });
    }, 3000);
  };
};

export const useOptimisticUpdateQuery = (key: QueryKey) => {
  const queryClient = useQueryClient();

  return (comment: OptimisticRecordsCommentProps) => {
    const { user_email, comment: text, category } = comment;

    const date = new Date().toISOString();
    const tempComment = createNewComment({
      date,
      text,
      user_email,
      category,
      translate: true,
    });
    queryClient.setQueryData(key, (previousValue?: Records) => {
      previousValue?.messages.push(tempComment);
      return previousValue;
    });

    return () => {
      queryClient.setQueryData(key, (previousValue?: Records) => {
        if (!previousValue) return previousValue;
        return {
          ...previousValue,
          messages: previousValue?.messages.filter(({ date }) => date !== tempComment.date),
        };
      });
    };
  };
};

export const useRecordsQuery = (
  { eventId }: RecordsProps,
  queryOptions?: GenericQueryOptions<Records>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  return useQuery(
    ['RECORDS', eventId],
    get<Records>(EVENT_RECORDS(workspaceId, eventId)),
    queryOptions,
  );
};

export const useSummaryQuery = (
  { eventId }: RecordsProps,
  queryOptions?: GenericQueryOptions<Summary>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
  const formattedEventId = eventId?.replace('-CLONE', '');
  return useQuery(
    ['SUMMARY', 'RECORDS', eventId],
    get<Summary>(SUMMARY_RECORDS(workspaceId, formattedEventId)),
    queryOptions,
  );
};

export const useCommentMutation = () =>
  useMutation(({ eventId, text, category }: CommentProps) => {
    const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) as string;
    return post<Partial<Message>, { id: string }>(EVENT_RECORDS(workspaceId, eventId), {
      payload: { text, category },
    });
  });
